/**
 * The visit page's rail (340; 280 on an iPad) — `docs/visit-page.md` §7.3,
 * do-not-substitute 4 and 18; Boards 1, 13 A, 14, 15, 17 H, 18 C, 18 F:
 * **About · Visits on <job> · People · Purchase orders · Close-out**, in that
 * order. Everything drawn is the server's (`VisitRail`): the rail orders,
 * folds and counts nothing.
 */
import { useState, type ReactNode } from 'react'
import type {
  AboutCard as AboutData, CloseOutCard as CloseOutData, CloseOutDayLine, JobVisitFold, JobVisitRow,
  JobVisitsCard as JobVisitsData, PeopleCard as PeopleData, PeopleRow, PurchaseOrdersCard as PurchaseOrdersData,
  RailQuoteCheck, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { StatusChip } from '../components/primitives.js'
import { BoardCard } from './card.js'
import { COMMON, HEADER, RAIL, SERIES } from './copy.js'
import { Face } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { VisitMark } from './visit-mark.js'
import { dayRange, dayText, hours1, localTime, money, telHref, smsHref, timeIn } from './format.js'

export interface RailHandlers {
  /** About's **Edit** — the visit's edit sheet (§7.3). */
  onEdit?: () => void
  /** Open a record of the app (a job, a visit, a PO, Purchasing). */
  onOpen: (href: string) => void
  /** **Text** to the customer — the Activity text sheet (§7.3). */
  onTextContact?: (contactId: string) => void
  /** Close-out's **Close out** on a day the crew app could not close (Board 18 F). */
  onCloseOut?: (day: number) => void
}

export function VisitRailCards({ read, handlers }: { read: VisitRead; handlers: RailHandlers }) {
  const { rail } = read
  return (
    <div className="flex flex-col gap-4 leading-[normal] tabular-nums" data-visit-rail="">
      <AboutCardView about={rail.about} read={read} handlers={handlers} />
      <JobVisitsCardView card={rail.jobVisits} handlers={handlers} />
      {/* A series occurrence's rail is About and Visits on <job>, no more (§18, Board 14). */}
      {!read.series && (
        <>
          {/* A closed visit's rail is About · Visits · Close-out — nobody left to call (Board 13 A; lead, Oct 29). */}
          {read.status !== 'closed' && <PeopleCardView card={rail.people} handlers={handlers} />}
          {rail.purchaseOrders && <PurchaseOrdersCardView card={rail.purchaseOrders} read={read} handlers={handlers} />}
          <CloseOutCardView card={rail.closeOut} read={read} handlers={handlers} />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------

function AboutRow({ label, children, link = false, onClick }: { label: string; children: ReactNode; link?: boolean; onClick?: () => void }) {
  return (
    <div className="flex gap-2.5 py-[5px] text-[13px] leading-[1.35]">
      <span className="w-24 shrink-0 text-text-muted">{label}</span>
      {onClick
        ? (
          <button type="button" onClick={onClick}
            className={cn('min-w-0 flex-1 text-left', link ? 'text-accent hover:text-accent-dark' : 'text-text')}>
            {children}
          </button>
        )
        : <span className={cn('min-w-0 flex-1', link ? 'text-accent' : 'text-text')}>{children}</span>}
    </div>
  )
}

function AboutCardView({ about, read, handlers }: { about: AboutData; read: VisitRead; handlers: RailHandlers }) {
  const billingWord = HEADER.billing[about.billing.kind]
  const billing = about.billing.kind === 'time_materials'
    ? (about.billing.internal ? RAIL.about.billingLine(billingWord, COMMON.per(money(about.billing.internal.billRate), 'h')) : billingWord)
    : about.billing.price === null
      ? billingWord
      : about.billing.invoiced === 'monthly'
        ? RAIL.about.billingMonthly(billingWord, money(about.billing.price))
        : RAIL.about.billingLine(billingWord, money(about.billing.price))
  const days = about.days
  return (
    <BoardCard
      title={RAIL.about.title}
      rail
      action={about.canEdit && handlers.onEdit ? { label: RAIL.about.edit, glyph: 'pencil', onClick: handlers.onEdit } : null}
      bodyClassName="px-[18px] pb-3.5 pt-2.5"
      asDrawn="visit-rail about"
    >
      <AboutRow label={RAIL.about.visit}>
        {about.series && about.series.count !== null
          ? RAIL.about.seriesVisit(about.reference, String(about.series.index), String(about.series.count))
          : about.reference}
      </AboutRow>
      <AboutRow label={RAIL.about.job} link onClick={() => handlers.onOpen(`/jobs/${about.job.id}`)}>
        {COMMON.dot(about.job.reference, about.job.name)}
      </AboutRow>
      {days && (days.count > 1
        ? (
          <AboutRow label={RAIL.about.days}>
            {COMMON.dot(String(days.count), dayRange(days.first, days.last, 'long'))}
          </AboutRow>
        )
        : (
          <AboutRow label={RAIL.about.day}>
            {days.window
              ? COMMON.dot(dayText(days.first, 'date'), COMMON.range(localTime(days.window.from), localTime(days.window.to)))
              : dayText(days.first, 'date')}
          </AboutRow>
        ))}
      {about.crew && (
        <AboutRow label={RAIL.about.crew}>
          {about.crew.lead ? RAIL.about.crewLead(about.crew.crew.name, about.crew.lead.name) : about.crew.crew.name}
        </AboutRow>
      )}
      {about.division && !read.series && <AboutRow label={RAIL.about.division}>{about.division.name}</AboutRow>}
      <AboutRow label={RAIL.about.billing}>{billing}</AboutRow>
      <AboutRow label={RAIL.about.planned}>
        {about.planned.quoteReference
          ? RAIL.about.plannedFrom(hours1(about.planned.hours), about.planned.quoteReference)
          : COMMON.h(hours1(about.planned.hours))}
      </AboutRow>
      {/* Nothing leaves for a closed visit (Board 13 A); a series' About is §18's rows, without it (Board 14). */}
      {about.yard && !read.series && read.status !== 'closed' && <AboutRow label={RAIL.about.leavesFrom}>{about.yard.name}</AboutRow>}
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// Visits on <job> — §7.3, revision 2
// ---------------------------------------------------------------------------

/** The line under a row's name — *V-1188 · not scheduled · from Apr 15 · 6.0 h* (§7.3). */
export function jobVisitLine(row: JobVisitRow): string {
  if (row.cancelled) return COMMON.dot(row.reference, COMMON.cancelled)
  const { when } = row
  const whenText = when.kind === 'unscheduled' ? null
    : when.kind === 'day' ? (when.relative === 'today' ? COMMON.today : dayText(when.date, 'date'))
      : dayRange(when.first, when.last, 'short')
  if (row.thisVisit && row.dayOf && whenText) {
    return RAIL.jobVisits.lineToday(row.reference, whenText, String(row.dayOf.index), String(row.dayOf.count))
  }
  if (when.kind === 'unscheduled') {
    return RAIL.jobVisits.lineUnscheduled(row.reference, when.from ? dayText(when.from, 'monthDay') : '', hours1(row.hours?.hours ?? 0))
  }
  const at = whenText ?? ''
  if (row.closed) {
    return row.hours ? RAIL.jobVisits.lineClosedHours(row.reference, at, hours1(row.hours.hours)) : RAIL.jobVisits.lineClosed(row.reference, at)
  }
  if (row.hours?.kind === 'so_far') return RAIL.jobVisits.lineSoFar(row.reference, at, hours1(row.hours.hours))
  if (row.hours) return RAIL.jobVisits.linePlanned(row.reference, at, hours1(row.hours.hours))
  return COMMON.dot(row.reference, at)
}

export function JobVisitRowView({ row, phone = false, onOpen }: { row: JobVisitRow; phone?: boolean; onOpen: (href: string) => void }) {
  const name = row.series && row.series.count !== null
    ? SERIES.name(row.name, String(row.series.index), String(row.series.count))
    : row.name
  const line = jobVisitLine(row)
  const body = (
    <>
      <VisitMark mark={row.cancelled ? { ...row.mark, status: 'cancelled' } : row.mark} size={28} />
      <div className="flex min-w-0 flex-1 flex-col">
        <b className={cn('truncate font-semibold', phone ? 'text-[14px]' : 'text-[13px]', row.cancelled && 'line-through')}>{name}</b>
        <span className={cn('truncate text-text-muted', phone ? 'mt-0.5 text-[12px]' : 'mt-px text-[11.5px]')}>
          {row.thisVisit
            ? <><b className="font-bold text-accent-dark">{RAIL.jobVisits.thisVisit}</b>{' '}{RAIL.jobVisits.afterThisVisit(line)}</>
            : line}
        </span>
      </div>
      {!row.thisVisit && <span className="inline-flex text-text-faint"><VisitGlyph name="chevronRight" size={14} stroke={2} /></span>}
    </>
  )
  const className = cn('box-border flex w-full items-center gap-[11px] border-t border-line-faint text-left',
    phone ? 'min-h-[58px] px-3.5 py-3' : 'min-h-[52px] px-[18px] py-[7px]',
    row.thisVisit ? 'bg-green-band-bg' : 'hover:bg-hover')
  return row.thisVisit
    ? <div className={className} data-this-visit="">{body}</div>
    : <button type="button" className={className} onClick={() => onOpen(`/visits/${row.id}`)}>{body}</button>
}

function FoldRow({ fold, onOpenFold }: { fold: JobVisitFold; onOpenFold: () => void }) {
  const words = fold.through
    ? RAIL.jobVisits.foldThrough(String(fold.from), String(fold.to), dayText(fold.through, 'monthDay'))
    : RAIL.jobVisits.fold(String(fold.from), String(fold.to), String(fold.closed), String(fold.skipped))
  return (
    <button type="button" onClick={onOpenFold}
      className="flex w-full items-center gap-2 border-t border-line-faint px-[18px] py-[9px] text-left text-[12px] text-text-muted hover:bg-hover">
      <VisitGlyph name="chevronDown" size={13} stroke={2} />
      {words}
    </button>
  )
}

export function QuoteCheckLine({ check, onOpen, jobId }: { check: RailQuoteCheck; onOpen: (href: string) => void; jobId: string }) {
  if (check.missing === 0) {
    return (
      <div className="flex items-center gap-[7px] border-t border-line-faint bg-surface px-[18px] py-2.5 text-[12px] text-accent-dark" data-quote-check="whole">
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent text-white">
          <VisitGlyph name="check" size={10} stroke={3} />
        </span>
        {RAIL.jobVisits.checkWhole(check.reference, String(check.present), String(check.expected))}
      </div>
    )
  }
  const sentence = check.missing === 1
    ? RAIL.jobVisits.checkMissingOne(check.reference, String(check.expected), String(check.missing))
    : RAIL.jobVisits.checkMissing(check.reference, String(check.expected), String(check.missing))
  return (
    <div className="flex items-start gap-[7px] border-t border-danger-line bg-danger-bg px-[18px] py-2.5 text-[12px] text-danger" data-quote-check="missing">
      <span className="mt-px inline-flex"><VisitGlyph name="warn" size={14} stroke={2.2} /></span>
      <span>
        <b>{sentence}</b>{' '}
        <button type="button" onClick={() => onOpen(`/jobs/${jobId}?tab=visits`)} className="font-semibold text-accent hover:text-accent-dark">
          {RAIL.jobVisits.link}
        </button>
      </span>
    </div>
  )
}

/** The rows and folds in order — a fold opened shows its rows as ordinary rows, in place (§7.3, §27.7). */
export function JobVisitsList({ card, phone = false, onOpen }: { card: JobVisitsData; phone?: boolean; onOpen: (href: string) => void }) {
  const [open, setOpen] = useState<Set<number>>(new Set())
  const items: ReactNode[] = []
  const foldsAt = (index: number) => card.folds.filter((fold) => fold.insertAt === index)
  for (let index = 0; index <= card.rows.length; index += 1) {
    for (const fold of foldsAt(index)) {
      if (open.has(fold.from)) {
        for (const row of fold.rows) items.push(<JobVisitRowView key={row.id} row={row} phone={phone} onOpen={onOpen} />)
      } else {
        items.push(<FoldRow key={`fold-${fold.from}`} fold={fold} onOpenFold={() => setOpen((was) => new Set(was).add(fold.from))} />)
      }
    }
    const row = card.rows[index]
    if (row) items.push(<JobVisitRowView key={row.id} row={row} phone={phone} onOpen={onOpen} />)
  }
  return (
    <>
      {items}
      {card.checks.map((check) => <QuoteCheckLine key={check.quoteId} check={check} jobId={card.job.id} onOpen={onOpen} />)}
    </>
  )
}

function JobVisitsCardView({ card, handlers }: { card: JobVisitsData; handlers: RailHandlers }) {
  return (
    <BoardCard
      title={RAIL.jobVisits.title(card.job.reference)}
      count={card.count}
      rail
      action={{ label: RAIL.jobVisits.link, glyph: 'chevronRight', onClick: () => handlers.onOpen(`/jobs/${card.job.id}?tab=visits`) }}
      bodyClassName="py-1"
      asDrawn="vp-17 H"
    >
      <JobVisitsList card={card} onOpen={handlers.onOpen} />
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

function PersonButton({ glyph, children, href, onClick }: { glyph: 'phone' | 'bubble'; children: ReactNode; href?: string; onClick?: () => void }) {
  const className = 'inline-flex h-7 items-center justify-center gap-1.5 whitespace-nowrap rounded-button border border-line bg-surface px-[9px] text-[12px] font-semibold text-text hover:bg-hover'
  const inner = <><VisitGlyph name={glyph} size={14} stroke={2} />{children}</>
  return href ? <a href={href} className={className}>{inner}</a> : <button type="button" onClick={onClick} className={className}>{inner}</button>
}

function personLine(row: PeopleRow): string {
  if (row.role === 'customer') return row.phone ? RAIL.people.customer(row.phone) : RAIL.people.customer('').replace(/ · $/, '')
  if (row.role === 'crew_lead') return RAIL.people.crewLead(row.crew.name)
  return RAIL.people.salesperson(row.quoteReference ?? '')
}

function PeopleCardView({ card, handlers }: { card: PeopleData; handlers: RailHandlers }) {
  return (
    <BoardCard title={RAIL.people.title} count={card.count} rail bodyClassName="py-1">
      {card.people.map((row, index) => (
        <div key={`${row.role}-${row.person.id}`} className={cn('flex gap-[11px] px-[18px] py-2.5', index > 0 && 'border-t border-line-faint')}>
          <Face person={row.person} size={34} customer={row.role === 'customer'} />
          <div className="flex flex-col">
            <b className="text-[13px] font-bold">{row.person.name}</b>
            <span className="text-[11.5px] text-text-muted">{personLine(row)}</span>
            {row.role !== 'salesperson' && row.phone && (
              <div className="mt-1.5 flex gap-1.5">
                <PersonButton glyph="phone" href={telHref(row.phone)}>{RAIL.people.call}</PersonButton>
                {row.role === 'customer' && handlers.onTextContact
                  ? <PersonButton glyph="bubble" onClick={() => handlers.onTextContact?.(row.person.id)}>{RAIL.people.text}</PersonButton>
                  : <PersonButton glyph="bubble" href={smsHref(row.phone)}>{RAIL.people.text}</PersonButton>}
              </div>
            )}
          </div>
        </div>
      ))}
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// Purchase orders
// ---------------------------------------------------------------------------

const PO_TILE: Record<string, string> = {
  delivered: 'visitPo-delivered',
  ready_for_pickup: 'visitPo-ready_for_pickup',
  placed: 'visitPo-placed',
}

function PurchaseOrdersCardView({ card, read, handlers }: { card: PurchaseOrdersData; read: VisitRead; handlers: RailHandlers }) {
  const purchasingOn = read.capabilities['purchasing.orders']
  return (
    <BoardCard
      title={RAIL.purchaseOrders.title}
      count={card.count}
      rail
      action={purchasingOn ? { label: RAIL.purchaseOrders.link, onClick: () => handlers.onOpen(`/purchasing?visit=${encodeURIComponent(read.id)}`) } : null}
      bodyClassName="py-1"
      asDrawn="vp-18 C rail"
    >
      {card.sharedVendor && (
        <div className="px-[18px] pb-2 pt-0.5 text-[11.5px] text-text-muted">
          {card.allNoted ? RAIL.purchaseOrders.allWithNoted(card.sharedVendor.name) : RAIL.purchaseOrders.allWith(card.sharedVendor.name)}
        </div>
      )}
      {card.rows.map((row) => {
        const key = `--status-${PO_TILE[row.po.status] ?? `visitPo-${row.po.status}`}`
        const when = row.when === null ? null
          : row.when.kind === 'pickup' ? timeIn(read.timeZone, row.when.at, 'dayClock')
            : row.when.relative === 'today' ? COMMON.today : dayText(row.when.on, 'date')
        const what = row.what.join(', ')
        const status = row.po.status as keyof typeof RAIL.purchaseOrders.status
        return (
          <button
            key={row.po.id}
            type="button"
            onClick={() => handlers.onOpen(`/purchasing/purchase-orders?po=${encodeURIComponent(row.po.id)}`)}
            className="box-border flex min-h-[52px] w-full items-center gap-[11px] border-t border-line-faint px-[18px] py-[7px] text-left hover:bg-hover"
          >
            <span
              className="box-content inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-button border"
              style={{ background: `rgb(var(${key}-bg))`, color: `rgb(var(${key}-fg))`, borderColor: `color-mix(in srgb, rgb(var(${key}-fg)) 22%, rgb(var(${key}-bg)))` }}
            >
              <VisitGlyph name="cart" size={15} stroke={1.9} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="flex items-center gap-1.5">
                <b className="whitespace-nowrap text-[13px] font-semibold">{row.po.number}</b>
                {row.po.entry === 'noted' && (
                  <span className="inline-flex h-[19px] items-center rounded-tag bg-line-soft px-1.5 text-[11px] font-semibold text-text-muted">
                    {RAIL.purchaseOrders.noted}
                  </span>
                )}
              </span>
              <span className="mt-px text-[11.5px] text-text-muted">{when ? RAIL.purchaseOrders.line(what, when) : what}</span>
            </div>
            <StatusChip group="visitPo" status={row.po.status} className="shrink-0 whitespace-nowrap !gap-[5px]">
              {status in RAIL.purchaseOrders.status ? RAIL.purchaseOrders.status[status] : row.po.status}
            </StatusChip>
            <span className="inline-flex text-text-faint"><VisitGlyph name="chevronRight" size={14} stroke={2} /></span>
          </button>
        )
      })}
    </BoardCard>
  )
}

// ---------------------------------------------------------------------------
// Close-out
// ---------------------------------------------------------------------------

function DayDot({ state }: { state: CloseOutDayLine['state'] | 'office' }) {
  if (state === 'closed_out' || state === 'office') {
    return (
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-white">
        <VisitGlyph name={state === 'office' ? 'lock' : 'check'} size={state === 'office' ? 10 : 11} stroke={3} />
      </span>
    )
  }
  if (state === 'on_site') {
    return (
      <span className="box-border inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-teal-band-text">
        <i className="block h-2 w-2 rounded-full bg-teal-band-text" />
      </span>
    )
  }
  if (state === 'not_closed_out') {
    return (
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warn-bg text-warn">
        <VisitGlyph name="warn" size={12} stroke={2.4} />
      </span>
    )
  }
  return <span className="box-border inline-block h-5 w-5 shrink-0 rounded-full border-2 border-line-strong" />
}

function CloseOutCardView({ card, read, handlers }: { card: CloseOutData; read: VisitRead; handlers: RailHandlers }) {
  const tz = read.timeZone
  const crewCloses = read.capabilities['crew.closeout']
  const oneDayClosed = card.closedByOffice !== null && card.days.length === 1
  return (
    <BoardCard title={RAIL.closeOut.title} rail bodyClassName="px-[18px] pb-3.5 pt-2.5" asDrawn="vp-18 F rail-card">
      {oneDayClosed && card.closedByOffice ? (
        <>
          {card.days[0]?.closedOut && (
            <Line state="closed_out" tight title={RAIL.closeOut.closedOutByTheCrew}
              sub={COMMON.dot(card.days[0].closedOut.by.name, timeIn(tz, card.days[0].closedOut.at, 'dayAmpm'))} bold />
          )}
          <Line state="office" tight title={RAIL.closeOut.closedByTheOffice}
            sub={COMMON.dot(card.closedByOffice.by.name, timeIn(tz, card.closedByOffice.at, 'dayAmpm'))} bold />
        </>
      ) : (
        card.days.map((line) => {
          const n = String(line.day.index)
          if (line.state === 'closed_out' && line.closedOut) {
            return (
              <Line key={n} state="closed_out" title={RAIL.closeOut.closedOutByCrew(n)}
                sub={COMMON.dot(line.closedOut.enteredBy ? line.closedOut.by.name : line.closedOut.by.name, timeIn(tz, line.closedOut.at, 'dayAmpm'))} />
            )
          }
          if (line.state === 'on_site') {
            return (
              <Line key={n} state="on_site" title={RAIL.closeOut.onSiteNow(n)}
                sub={crewCloses ? RAIL.closeOut.crewClosesIt : RAIL.closeOut.officeClosesIt} />
            )
          }
          if (line.state === 'not_closed_out') {
            return (
              <Line key={n} state="not_closed_out" title={RAIL.closeOut.notClosedOut(n)}
                sub={line.endedAt ? RAIL.closeOut.ended(timeIn(tz, line.endedAt, 'dayAmpm')) : ''}
                action={handlers.onCloseOut ? (
                  <button type="button" onClick={() => handlers.onCloseOut?.(line.day.index)}
                    className="inline-flex h-7 shrink-0 items-center justify-center whitespace-nowrap rounded-button border border-line bg-surface px-3.5 text-[12px] font-semibold text-text hover:bg-hover">
                    {RAIL.closeOut.closeOut}
                  </button>
                ) : null}
              />
            )
          }
          return (
            <Line key={n} state={line.state} title={RAIL.closeOut.upcoming(n, dayText(line.day.date, 'date'))}
              sub={line.last ? RAIL.closeOut.lastDay : ''} />
          )
        })
      )}
      {card.stillOpen && (
        <div className="mt-2 rounded-[10px] border border-line-faint bg-surface-alt px-[11px] py-[9px] text-[12px] leading-[1.45] text-text-body">
          <b>{RAIL.closeOut.then}</b>{' '}
          {RAIL.closeOut.stillOpen(String(card.stillOpen.receiptsToReview))}
        </div>
      )}
    </BoardCard>
  )
}

function Line({ state, title, sub, action, tight = false, bold = false }: {
  state: CloseOutDayLine['state'] | 'office'; title: string; sub: string; action?: ReactNode; tight?: boolean; bold?: boolean
}) {
  return (
    <div className={cn('flex items-start gap-2.5', tight ? 'py-1' : 'py-1.5')}>
      <DayDot state={state} />
      <div className="flex flex-1 flex-col">
        <b className={cn('text-[13px]', bold ? 'font-bold' : 'font-semibold')}>{title}</b>
        {sub && <span className="text-[11.5px] text-text-muted">{sub}</span>}
      </div>
      {action}
    </div>
  )
}
